import React, { Fragment, useMemo, useState } from "react";
import { useClasses } from "@api/class.api";
import {
    isRuleVisible,
    isRuleVisibleForFeatures,
} from "@components/settings/Rules/utils";
import { isRuleHiddenForGlFirst } from "@common/types/transactionRule";
import { useCategoryLabelGetter } from "@hooks/useCategoryLabelGetter";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { useEntities } from "@hooks/useEntities";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { Loader } from "@design-system/Loader/Loader";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useWorkspaceRuleGroups } from "@api/rules.api";
import {
    FILTER_PICKER_LIST_CLASS_NAMES,
    FilterPickerListActions,
    FilterPickerListFrame,
} from "@components/pageHeader/filters/FilterPickerList/FilterPickerListParts";
import { BaseFilterProps } from "../../lib";
import { TransactionRuleConditions } from "../../../../transactionRules/TransactionRuleConditions";
import { TransactionRuleActions } from "../../../../transactionRules/TransactionRuleActions";
import styles from "./TransactionRulesFilter.module.scss";
import { getSearchableLabel } from "./helpers";

export const TransactionRulesFilter: React.FC<BaseFilterProps> = ({
    filters,
    onChange,
}) => {
    const ruleGroups = useWorkspaceRuleGroups();

    const [search, setSearch] = useState("");
    const labelGetter = useCategoryLabelGetter();
    const financialAccounts = useFinancialAccounts();
    const entities = useEntities();
    const classes = useClasses();
    const { enabled: isGlFirst } = useIsGlFirst();
    const { schedules: isSchedulesEnabled } = useTypedFlags();

    const rules = useMemo(() => {
        if (!ruleGroups.data) {
            return [];
        }

        const { matchingCounts } = ruleGroups.data;
        return ruleGroups.data.ruleGroups
            .flatMap((g) => g.rules ?? [])
            .filter(
                (r) =>
                    isRuleVisible(r, matchingCounts) &&
                    !isRuleHiddenForGlFirst(r, { isGlFirst }) &&
                    isRuleVisibleForFeatures(r, { isSchedulesEnabled }),
            );
    }, [ruleGroups.data, isGlFirst, isSchedulesEnabled]);

    const rulesWithSearchableLabels = useMemo(
        () =>
            rules.map((rule) => ({
                rule,
                label: getSearchableLabel(rule, {
                    labelGetter,
                    financialAccounts,
                    entities,
                    counterparties: ruleGroups.data?.counterparties ?? [],
                    classesMap: classes.classesMap,
                }).toLowerCase(),
            })),
        [
            rules,
            labelGetter,
            financialAccounts,
            entities,
            ruleGroups.data?.counterparties,
            classes.classesMap,
        ],
    );

    const displayedRules = useMemo(() => {
        if (!search) {
            return rulesWithSearchableLabels.map(({ rule }) => rule);
        }

        return rulesWithSearchableLabels
            .filter(({ label }) => label.includes(search.toLowerCase()))
            .map(({ rule }) => rule);
    }, [search, rulesWithSearchableLabels]);

    if (ruleGroups.isPending) {
        return <Loader />;
    }

    if (!rules.length) {
        return (
            <p className="text-center mt-5 text-prominent">
                No rules created yet
            </p>
        );
    }

    const selectedCount = filters.transactionRulesIds?.length ?? 0;

    return (
        <div className={styles.filter}>
            <FilterPickerListFrame>
                <Picker.Select
                    inline
                    multiple
                    value={filters.transactionRulesIds ?? []}
                    onValueChange={(transactionRulesIds) =>
                        onChange({ transactionRulesIds })
                    }
                    inputValue={search}
                    onInputValueChange={setSearch}
                    classNames={FILTER_PICKER_LIST_CLASS_NAMES}
                >
                    <Picker.Search autoFocus placeholder="Search..." />
                    <Picker.List>
                        {displayedRules.map((rule, index) => (
                            <Fragment key={rule.id}>
                                {index > 0 && <Picker.Separator />}
                                <Picker.Item value={rule.id} wrap>
                                    <div className={styles.rule}>
                                        <TransactionRuleConditions
                                            rule={rule}
                                            counterparties={
                                                ruleGroups.data
                                                    ?.counterparties ?? []
                                            }
                                            normalizedDescriptions={
                                                ruleGroups.data
                                                    ?.normalizedDescriptions ??
                                                []
                                            }
                                        />
                                        <TransactionRuleActions
                                            rule={rule}
                                            counterparties={
                                                ruleGroups.data
                                                    ?.counterparties ?? []
                                            }
                                        />
                                    </div>
                                </Picker.Item>
                            </Fragment>
                        ))}
                        {displayedRules.length === 0 && (
                            <Picker.Empty>No matching rules</Picker.Empty>
                        )}
                        {selectedCount > 0 && (
                            <Picker.Footer>
                                <FilterPickerListActions
                                    selectedCount={selectedCount}
                                    onDeselect={() =>
                                        onChange({
                                            transactionRulesIds: undefined,
                                        })
                                    }
                                />
                            </Picker.Footer>
                        )}
                    </Picker.List>
                </Picker.Select>
            </FilterPickerListFrame>
        </div>
    );
};
